import { FunctionalComponent, h } from '@stencil/core';
import {
  FluidAlignment,
  FluidColor,
  FluidContextMenuLink,
  FluidContextMenuSection,
  FluidIconConfig,
  FluidIconKey,
  FluidMenuSectionStyle,
  FluidPopoverConfig,
  FluidPopoverTranslationOptions,
  FluidSize,
  FluidSortOrder,
  FluidTheme,
  FluidToggleType,
} from '@lmig/fluid-core';
import { mapConfigObjectToProp } from '../utils/mapping/mapping.utils';
import { FluidFunctionalComponentProps } from '../model/functional/fluid-functional-component-props.interface';
import { themeVar } from '../utils/themes/theme.variables';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import { FluidContextMenuTranslationOptions } from '../components/fluid-context-menu/fluid-context-menu';
import { FluidI18nStore } from '../components';

// ================================================================ //
// -- MaxLengthPopover
export interface MaxLengthPopoverProps extends FluidFunctionalComponentProps {
  maxLengthPopoverConfig?: FluidPopoverConfig;
  theme?: FluidTheme;
  overrideGlobalTheme?: boolean;
  stopPropagation?: boolean;
  i18nStore?: FluidI18nStore;
}

/**
 * Renders an popover that displays a message when the maxLength is reached.
 * @param props
 * @constructor
 */
export const MaxLengthPopover: FunctionalComponent<MaxLengthPopoverProps> = (
  props: MaxLengthPopoverProps
) => {
  const {
    maxLengthPopoverConfig,
    theme,
    stopPropagation,
    fluidTranslate,
    i18nStore,
  } = props;

  const _message: string =
    i18nStore?.translate(fluidTranslate?.maxLengthPopoverConfig?.message) ||
    maxLengthPopoverConfig?.message ||
    'You have reached the maximum length allowed.';

  return (
    (!!_message || !!fluidTranslate) && (
      <fluid-popover
        {...mapConfigObjectToProp(props, 'maxLengthPopoverConfig', 'config')}
        theme={theme}
        fluidTranslate={fluidTranslate}
        overrideGlobalTheme={!!theme}
        onClick={(e) => stopPropagation && e.stopPropagation()}
        class={{
          'max-length-popover': true,
          [`${props.class}`]: !!props.class,
        }}>
        <div
          slot='content'
          style={{ 'white-space': 'normal' }}
          innerHTML={_message}
        />
      </fluid-popover>
    )
  );
};

// ================================================================ //
// -- InfoPopover

export interface InfoPopoverProps extends FluidFunctionalComponentProps {
  helpText: string | FluidPopoverConfig;
  maxLengthPopoverConfig?: FluidPopoverConfig;
  theme?: FluidTheme;
  iconColor?: FluidColor;
  overrideGlobalTheme?: boolean;
  onPopoverToggled?: (event) => void;
  stopPropagation?: boolean;
  fluidTranslate?: FluidPopoverTranslationOptions;
}

/**
 * Renders an info icon with popover
 * @param props
 * @constructor
 */
export const InfoPopover: FunctionalComponent<InfoPopoverProps> = (
  props: InfoPopoverProps
) => {
  const { helpText, theme, iconColor, stopPropagation, fluidTranslate } = props;
  const _message: string =
    typeof helpText === 'string' ? helpText : helpText?.message;
  return (
    (!!_message || !!fluidTranslate) && (
      <fluid-popover
        {...mapConfigObjectToProp(props, 'helpText', 'config')}
        theme={theme}
        fluidTranslate={fluidTranslate}
        overrideGlobalTheme={!!theme}
        onClick={(e) => stopPropagation && e.stopPropagation()}
        class={{
          'info-popover': true,
          [`${props.class}`]: !!props.class,
        }}>
        <fluid-icon
          slot='trigger'
          class='info-popover-icon'
          size={FluidSize.EXTRA_SMALL}
          iconKey={FluidIconKey.INFO}
          color={iconColor}
          theme={theme}
          overrideGlobalTheme={!!theme}
          interactive={true}
          inline={true}
        />
        <div
          slot='content'
          style={{ 'white-space': 'normal' }}
          innerHTML={_message}
        />
      </fluid-popover>
    )
  );
};

// ================================================================ //
// -- clearSelectionIcon

interface ClearSelectionProps {
  onClicked: () => void;
  disabled: boolean;
  popoverMessage: string;
}

export const ClearSelectionButton = (props: ClearSelectionProps) => {
  const { onClicked, disabled, popoverMessage } = props;
  return (
    <fluid-button
      config={{
        icon: {
          key: FluidIconKey.UNCHECK,
          size: FluidSize.SMALL,
        },
      }}
      onClick={() => onClicked()}
      disabled={disabled}
      small={true}
      popoverConfig={{
        message: popoverMessage,
      }}
    />
  );
};

interface ToggleProps {
  toggleConfigProps: FluidIconConfig;
  disabled: boolean;
  toggleAllValue: any;
  toggleType: FluidToggleType;
  toggleRef: any;
}

export const ToggleSelectAll = (props: ToggleProps) => {
  const { toggleConfigProps, disabled, toggleAllValue, toggleType, toggleRef } =
    props;
  return (
    <fluid-toggle
      config={toggleConfigProps}
      class='header-cell-toggle'
      control-name='table-toggle-all'
      disabled={disabled}
      toggled={toggleAllValue}
      toggleType={toggleType}
      removePadding={true}
      ref={toggleRef}
    />
  );
};

// ================================================================ //
// -- DeleteIcon

interface DeleteIconProps extends FluidFunctionalComponentProps {
  doRender: boolean;
  handleDelete: Function;
  disabled: boolean;
  theme?: FluidTheme;
  color?: FluidColor;
  size?: FluidSize;
  asButton?: boolean;
  identifier?: string;
}

/**
 * Renders a delete icon with onClick action handler
 * @param props
 * @constructor
 */
export const DeleteIcon: FunctionalComponent<DeleteIconProps> = (
  props: DeleteIconProps
) => {
  const {
    doRender,
    handleDelete,
    disabled,
    theme,
    color,
    size,
    asButton,
    identifier,
  } = props;

  if (!doRender) return;

  const config: FluidIconConfig = {
    key: FluidIconKey.DELETE,
    size: size ?? themeVar<FluidSize>(theme, 'icons.default_size'),
    color,
    ...(!!theme && {
      theme,
      overrideGlobalTheme: true,
    }),
    identifier,
  };

  const handleClick = () => {
    if (!handleDelete || disabled) return;
    handleDelete();
  };

  return !!asButton ? (
    <fluid-button
      identifier={identifier}
      icon={config}
      theme={theme}
      overrideGlobalTheme={!!theme}
      disabled={disabled}
      onClick={() => handleClick()}
      class={{
        'delete-icon': true,
        [`${props.class}`]: !!props.class,
      }}
    />
  ) : (
    <fluid-icon
      onClick={() => handleClick()}
      config={{
        ...config,
        interactive: !disabled,
        disabled,
      }}
      class={{
        'delete-icon': true,
        [`${props.class}`]: !!props.class,
      }}
    />
  );
};

// ================================================================ //
// -- SortIcon

interface SortIconProps extends FluidFunctionalComponentProps {
  doRender: boolean;
  sortDirection: FluidSortOrder;
  handleSort?: Function;
  theme?: FluidTheme;
  disabled?: boolean;
  color?: FluidColor;
}

/**
 * Renders a sort icon with icon based on sort direction.
 * @param props
 * @constructor
 */
export const SortIcon: FunctionalComponent<SortIconProps> = (
  props: SortIconProps
) => {
  const { doRender, handleSort, sortDirection, disabled, theme, color } = props;
  const iconKey =
    sortDirection === FluidSortOrder.ASC
      ? FluidIconKey.SORT_UP
      : FluidIconKey.SORT_DOWN;
  return (
    doRender && (
      <fluid-icon
        iconKey={iconKey}
        disabled={disabled}
        theme={theme}
        overrideGlobalTheme={!!theme}
        interactive={!disabled}
        color={color}
        size={FluidSize.EXTRA_SMALL}
        onClick={!!handleSort && (() => handleSort())}
        class={{
          'sort-icon': true,
          [`${props.class}`]: !!props.class,
        }}
      />
    )
  );
};

// ================================================================ //
// -- FilterIcon

interface FilterIconProps extends FluidFunctionalComponentProps {
  doRender: boolean;
  theme?: FluidTheme;
  disabled?: boolean;
  color?: FluidColor;
  size?: FluidSize;
  position?: any;
  filtersActive?: boolean;
}

/**
 * Renders a filter icon
 * @param props
 * @constructor
 */
export const FilterIcon: FunctionalComponent<FilterIconProps> = (
  props: FilterIconProps
) => {
  const { doRender, theme, disabled, color, size, position, filtersActive } =
    props;

  return (
    doRender && (
      <fluid-icon
        iconKey={FluidIconKey.FILTER}
        disabled={disabled}
        theme={theme}
        includeAdornment={filtersActive}
        overrideGlobalTheme={!!theme}
        interactive={!disabled}
        color={color}
        size={size}
        position={position}
        class={{
          'filter-icon': true,
          [`${props.class}`]: !!props.class,
        }}
      />
    )
  );
};

// ================================================================ //
// -- ExpansionToggle

interface ExpansionToggleProps extends FluidFunctionalComponentProps {
  open: boolean;
  disabled: boolean;
  theme: FluidTheme;
  doRender: boolean;
  iconKey?: FluidIconKey;
}

/**
 * Renders an expansion toggle, which flips based on open state.
 * @param props
 * @constructor
 */
export const ExpansionToggle: FunctionalComponent<ExpansionToggleProps> = (
  props: ExpansionToggleProps
) => {
  const { open, disabled, theme, doRender, iconKey } = props;

  return (
    doRender && (
      <fluid-icon
        class={!!props.class && `${props.class}`}
        config={{
          flipVertical: open,
          key: iconKey ?? FluidIconKey.CARET_DOWN,
          size: FluidSize.SMALL,
          overrideGlobalTheme: true,
          disabled,
          theme,
        }}
      />
    )
  );
};

// ================================================================ //
// -- InlineLoader

interface InlineLoaderProps extends FluidFunctionalComponentProps {
  doRender: boolean;
  theme: FluidTheme;
}

/**
 * Renders an inline loader which displays and rotates when doRender is true
 * @param props
 * @constructor
 */
export const InlineLoader: FunctionalComponent<InlineLoaderProps> = (
  props: InlineLoaderProps
) => {
  const { doRender, theme } = props;

  return (
    doRender && (
      <fluid-icon
        key={generateUniqueId()}
        config={{
          key: FluidIconKey.LOADER,
          size: FluidSize.SMALL,
          color: FluidColor.INFO,
          rotate: true,
          interactive: false,
          overrideGlobalTheme: true,
          theme,
        }}
        class={{
          'inline-loader': true,
          [`${props.class}`]: !!props.class,
        }}
      />
    )
  );
};

// ================================================================ //
// -- ContextMenuIconTrigger

interface ContextMenuIconTriggerProps extends FluidFunctionalComponentProps {
  doRender: boolean;
  menuId: string;
  menuSections: FluidContextMenuSection[];
  onToggleChanged: Function;
  onLinkClicked: Function;
  onContextMenuToggled: Function;
  closeLabel: string;
  setRef?: Function;
  alignment?: Exclude<FluidAlignment, FluidAlignment.CENTER>;
  compactFilters?: boolean;
  outsideClickClose?: boolean;
  displayMenuActions?: boolean;
  theme?: FluidTheme;
  stopPropagation?: boolean;
  fluidTranslate?: FluidContextMenuTranslationOptions;
}

export const ContextMenuIconTrigger: FunctionalComponent<
  ContextMenuIconTriggerProps
> = (props: ContextMenuIconTriggerProps, children) => {
  const {
    menuId,
    setRef,
    alignment,
    menuSections,
    onToggleChanged,
    closeLabel,
    onContextMenuToggled,
    onLinkClicked,
    doRender,
    compactFilters,
    outsideClickClose,
    displayMenuActions,
    theme,
    stopPropagation,
    fluidTranslate,
  } = props;

  return (
    doRender && (
      <fluid-context-menu
        fluidTranslate={fluidTranslate}
        closeLabel={closeLabel}
        menuId={menuId}
        class={!!props.class && `${props.class}`}
        menuSectionStyle={compactFilters && FluidMenuSectionStyle.INLINE}
        ref={(el) => setRef(el)}
        menuAlignment={alignment || FluidAlignment.RIGHT}
        menuSections={menuSections}
        outsideClickClose={outsideClickClose}
        displayMenuActions={displayMenuActions}
        onClick={(e) => stopPropagation && e.stopPropagation()}
        theme={theme}
        overrideGlobalTheme={!!theme}
        onContextMenuToggled={(event: CustomEvent<boolean>) => {
          onContextMenuToggled(event.detail);
        }}
        onLinkClicked={(event: CustomEvent<FluidContextMenuLink>) => {
          onLinkClicked(event.detail);
        }}
        onToggleChanged={(event: CustomEvent<FluidContextMenuSection[]>) => {
          onToggleChanged(event.detail);
        }}>
        {children}
      </fluid-context-menu>
    )
  );
};
